import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Logo from "../Assest/images/logo.png";
import Loader from "../components/Loader";
import getSymbolFromCurrency from 'currency-symbol-map';
import axios from "axios";
import { useCurrency } from "./CurrencyContext";

const GallerySlider = ({ data }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const { rate, currency } = useCurrency();

  const convertPrice = (price) => {
    const productPrice = parseFloat(price) || 0;
    const convertedPrice = productPrice * rate;
    return convertedPrice
  }
  const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;    
  
  const handleShopNow = useCallback(async (brandId, brandName) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/products/brand/${brandId}`);
      if (response.status === 200) {
        localStorage.setItem('apiProducts', JSON.stringify(response.data));

        navigate(`/product?brand=${brandName}&id=${brandId}`, {
          state: {
            apiData: response.data,
            brandId: brandId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching company ", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Format the product data properly to use in our component
  const products = data && data.length > 0 ? data.map(item => ({
    id: item.company_product_id,
    src: `${process.env.REACT_APP_API_URI}/uploads/${item.image}`,
    title: item.product_name,
    description: item.description,
    price: convertPrice(item.price).toFixed(2),
    currency_code: currencySymbol,
    rating: 4.7 + Math.random() * 0.3, // Generate random rating between 4.7-5.0
    brandId: item.brand_id,
    brandName: item.brand_name
  })) : [];

  // Auto-rotate the slider
  useEffect(() => {
    let interval;
    if (isAutoplay && products.length > 0) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [currentIndex, isAutoplay, products.length]);

  // If no data, show a message
  if (products.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-black to-gray-900">
        <div className="text-center text-white">
          <h3 className="text-xl font-light">No products available</h3>
          <p className="text-gray-400 mt-2">Please check back later for our luxury collection</p>
        </div>
      </div>
    );
  }

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % products.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? products.length - 1 : prevIndex - 1));
  };

  const pauseAutoplay = () => setIsAutoplay(false);
  const resumeAutoplay = () => setIsAutoplay(true);

  // Get visible items
  const getVisibleItems = () => {
    let items = [];
    const totalItems = products.length;

    // Current item
    items.push({
      item: products[currentIndex],
      position: 'center',
      index: currentIndex
    });

    // Previous item
    const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
    items.push({
      item: products[prevIndex],
      position: 'left',
      index: prevIndex
    });

    // Next item
    const nextIndex = (currentIndex + 1) % totalItems;
    items.push({
      item: products[nextIndex],
      position: 'right',
      index: nextIndex
    });

    return items;
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex">
        {[...Array(fullStars)].map((_, i) => (
          <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalfStar && (
          <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <defs>
              <linearGradient id="halfStarGradient">
                <stop offset="50%" stopColor="currentColor" />
                <stop offset="50%" stopColor="#D1D5DB" />
              </linearGradient>
            </defs>
            <path fill="url(#halfStarGradient)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
        {[...Array(5 - fullStars - (hasHalfStar ? 1 : 0))].map((_, i) => (
          <svg key={i + fullStars} className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-xs text-gray-400">({rating.toFixed(1)})</span>
      </div>
    );
  };

  const visibleItems = getVisibleItems();

  return (
    <div
      className="relative w-full h-screen overflow-hidden bg-gradient-to-b from-black to-gray-900 flex flex-col justify-center items-center"
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
    >
      {isLoading && <Loader Logo={Logo} />}
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-amber-500 blur-3xl"></div>
        <div className="absolute bottom-40 right-20 w-64 h-64 rounded-full bg-purple-700 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-blue-500 blur-3xl"></div>
      </div>

      {/* Gallery Header */}
      <div className="relative z-10 text-center mb-12 mt-8">
       <h2 className="text-white text-4xl font-light tracking-widest uppercase">DAILY ESSENTIALS</h2>
<p className="text-gray-400 text-sm mt-4 max-w-xl mx-auto italic">
  Explore our carefully curated selection of farm-fresh produce and pantry items, delivered straight to your home.
</p>
        <div className="w-24 h-0.5 bg-[#f36912] mx-auto mt-6"></div>
      </div>

      {/* Main Carousel */}
      <div className="relative w-full h-96 flex items-center justify-center mb-12">
        <AnimatePresence initial={false} custom={direction}>
          {visibleItems.map(({ item, position, index }) => {
            // Define positions
            let xPosition = 0;
            let scale = 1;
            let opacity = 1;
            let zIndex = 10;

            if (position === 'left') {
              xPosition = -350;
              scale = 0.85;
              opacity = 0.6;
              zIndex = 5;
            } else if (position === 'right') {
              xPosition = 350;
              scale = 0.85;
              opacity = 0.6;
              zIndex = 5;
            }

            return (
              <motion.div
                key={item.id}
                custom={direction}
                initial={{
                  x: direction > 0 ? 500 : -500,
                  opacity: 0,
                  scale: 0.8
                }}
                animate={{
                  x: xPosition,
                  opacity: opacity,
                  scale: scale,
                  zIndex: zIndex
                }}
                exit={{
                  x: direction > 0 ? -500 : 500,
                  opacity: 0,
                  scale: 0.8,
                  zIndex: 1
                }}
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.3 }
                }}
                className={`absolute flex h-90 w-64 ${position === 'center' ? 'md:w-96' : 'md:w-72'}`}
                style={{ zIndex }}
              >
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl w-full border border-gray-800">
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-40"></div>
                    <img
                      src={item.src}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = Logo; // Fallback to logo if image fails to load
                      }}
                    />

                  </div>

                  <div className="p-5">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-white text-lg font-medium tracking-wider">{item.title}</h3>
                      <span className="text-[#f36912] font-semibold whitespace-nowrap"> {item.currency_code} {item?.price}</span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                      {item.description}
                    </p>
                    {renderStars(item.rating)}

                    {position === 'center' && (
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mt-4 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black py-2 rounded-lg font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-300"
                        style={{ display: "block" }}
                        onClick={() => handleShopNow(item.brandId, item.brandName)}
                      >
                        DISCOVER
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black bg-opacity-30 backdrop-blur-sm border border-gray-600 text-white hover:bg-opacity-50 transition-all duration-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-black bg-opacity-30 backdrop-blur-sm border border-gray-600 text-white hover:bg-opacity-50 transition-all duration-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Pagination dots */}
      <div className="flex space-x-2 mb-12">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex ? "w-8 bg-[#f36912]" : "bg-gray-600"
              }`}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="text-center text-gray-500 text-xs tracking-wide">
        <p>CAREFULLY SELECTED DAILY ESSENTIALS</p>
      </div>
    </div>
  );
};

export default GallerySlider;

